import { useState } from 'react';
import type { Category } from '@mas-flow/types';
import { useCan } from '../auth/AuthProvider';
import { useCategories, useCreateCategory, usePatchCategory } from '../hooks/useCategories';
import { useToast } from '../hooks/useToast';
import { ApiHttpError } from '../api/client';

export function AdminCategoriesPage() {
  const canManage = useCan('settings.manage');
  if (!canManage) {
    return <p className="text-sm text-slate-500">Admin only.</p>;
  }
  return <AdminCategoriesInner />;
}

function AdminCategoriesInner() {
  const { data, isFetching } = useCategories();
  const create = useCreateCategory();
  const patch = usePatchCategory();
  const toast = useToast();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#10b981');

  const cats = data ?? [];
  const active = cats.filter((c) => !c.archived);
  const archived = cats.filter((c) => c.archived);

  const onArchive = (cat: Category) => {
    patch.mutate(
      { id: cat.id, patch: { archived: true }, etag: cat._etag },
      {
        onSuccess: () => toast(`Archived '${cat.name}'`),
        onError: (err) => {
          if (err instanceof ApiHttpError && err.status === 412) toast('Category changed elsewhere — refresh');
          else toast("Couldn't archive category");
        },
      },
    );
  };
  const onUnarchive = (cat: Category) => {
    patch.mutate(
      { id: cat.id, patch: { archived: false }, etag: cat._etag },
      {
        onSuccess: () => toast(`Restored '${cat.name}'`),
        onError: () => toast("Couldn't restore category"),
      },
    );
  };
  const onRename = (cat: Category, name: string) => {
    if (name === cat.name || !name.trim()) return;
    patch.mutate(
      { id: cat.id, patch: { name: name.trim() }, etag: cat._etag },
      {
        onSuccess: () => toast('Saved'),
        onError: (err) => {
          if (err instanceof ApiHttpError && err.status === 412) toast('Category changed elsewhere — refresh');
          else toast("Couldn't save category");
        },
      },
    );
  };
  const onColorChange = (cat: Category, color: string) => {
    if (color === cat.color) return;
    patch.mutate(
      { id: cat.id, patch: { color }, etag: cat._etag },
      {
        onSuccess: () => toast('Saved'),
        onError: () => toast("Couldn't save color"),
      },
    );
  };
  const onCreate = () => {
    const name = newName.trim();
    if (!name) return;
    create.mutate(
      { name, color: newColor },
      {
        onSuccess: (created) => { toast(`Created '${created.name}'`); setNewName(''); },
        onError: () => toast("Couldn't create category"),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">Categories</h1>
        <span className="text-sm text-slate-500">{cats.length} {cats.length === 1 ? 'category' : 'categories'}</span>
        {isFetching && <span className="text-xs text-slate-400">Loading…</span>}
      </div>
      <div className="rounded border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Color</th><th className="px-3 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {active.map((c) => (
              <Row key={c.id} cat={c} onRename={onRename} onColorChange={onColorChange} onArchive={() => onArchive(c)} />
            ))}
            {archived.map((c) => (
              <Row key={c.id} cat={c} dim onRename={onRename} onColorChange={onColorChange} onUnarchive={() => onUnarchive(c)} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="px-3 py-2">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New category name"
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
              </td>
              <td className="px-3 py-2">
                <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} aria-label="New category color" />
              </td>
              <td className="px-3 py-2 text-right">
                <button type="button" onClick={onCreate} disabled={!newName.trim() || create.isPending} className="rounded bg-brand px-3 py-1 text-xs text-white disabled:opacity-50">
                  Add
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

interface RowProps {
  cat: Category;
  dim?: boolean;
  onRename: (cat: Category, name: string) => void;
  onColorChange: (cat: Category, color: string) => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
}

function Row({ cat, dim, onRename, onColorChange, onArchive, onUnarchive }: RowProps) {
  return (
    <tr className={`border-b border-slate-100 ${dim ? 'opacity-50' : ''}`}>
      <td className="px-3 py-2">
        <input
          defaultValue={cat.name}
          onBlur={(e) => onRename(cat, e.target.value)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-sm"
          aria-label={`Rename ${cat.name}`}
        />
      </td>
      <td className="px-3 py-2">
        <input
          type="color"
          defaultValue={cat.color}
          onBlur={(e) => onColorChange(cat, e.target.value)}
          aria-label={`Color for ${cat.name}`}
        />
      </td>
      <td className="px-3 py-2 text-right">
        {onArchive && (
          <button type="button" onClick={onArchive} className="text-xs text-red-700 underline">Archive</button>
        )}
        {onUnarchive && (
          <button type="button" onClick={onUnarchive} className="text-xs text-green-700 underline">Unarchive</button>
        )}
      </td>
    </tr>
  );
}
